import { CheckIcon, FilterIcon, XIcon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CHALLAN_TYPES, type ChallanDirection, type ChallanType } from "@steelbill/types"
import { CustomerPicker } from "@/features/invoices/DocumentPickers"
import { useCustomer } from "@/features/customers/use-customer"
import type { useChallanFilters } from "@/features/challans/use-challan-list"
import { Badge } from "@/components/ui/badge"
import { DateField } from "@/components/DateField"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select"
import { RupeeBound } from "@/components/RupeeBound"
import { useFormatDay } from "@/lib/dates"
import { DATE_PRESETS, activeDatePreset } from "@/lib/date-presets"
import { formatRupees } from "@/lib/money"

/**
 * The delivery-challan register's filters, behind one button (§17.7, §11.1).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SAME SHEET AS THE OTHER TWO REGISTERS, PLUS THE TWO QUESTIONS ONLY A
 * CHALLAN RAISES. `ChallanListQuerySchema` is `.strict()` and declares `q`,
 * `sort`, `dir`, `status`, `challanType`, `openOnly`, `overdueOnly`,
 * `customerId`, `from`, `to`, `minTotalPaise`, `maxTotalPaise`, `limit` and
 * `offset` — nothing else. So there is no `issuedManually` here: that flag is
 * §6.1 and records that a TAX INVOICE was hand-written in the owner's book and
 * its number back-filled into the gap-free series. A challan has no such paper
 * series, `issued_manually` is a column on `model Invoice` alone, and a control
 * for it here would answer 400 rather than being politely ignored.
 *
 * THE AMOUNT RANGE BOUNDS `grand_total_paise`, WHICH IS WHAT THE ROW PRINTS —
 * and unlike the quotation register there is no second candidate. A challan has
 * no `tax_display_mode` (that column exists on `model Quotation` alone), so the
 * stored total and the printed total are one number. What it is NOT is money
 * owed: it is the Rule 55(1) declared value of goods in transit, which is why
 * the label is the neutral "Amount" — the same word the register's own total
 * column carries — and not "amount due".
 *
 * WHAT IS IN THE SHEET vs WHAT STAYS IN THE TOOLBAR. Status and the number
 * search stay outside, exactly as on the bill and quotation registers — the two
 * registers sit next to each other in the sidebar and a filter that lives in a
 * different place on each is a filter the owner has to hunt for twice. The
 * challan TYPE is in here rather than out there for the same reason the dates
 * are: three selects across a phone wraps the toolbar onto three rows and pushes
 * the register itself below the fold.
 *
 * "SEARCH BY NAME" IS A CUSTOMER PICKER, NOT A TEXT BOX. `q` on this endpoint
 * matches `challan_number` and `vehicle_number` and NOTHING else — the other
 * party lives in `customer_snapshot`, a text-backed JSON column the server will
 * not query inside (CLAUDE.md). The picker searches customers server-side and
 * filters by the `customerId` it returns, which covers the WHOLE book rather
 * than the fifty rows already on screen.
 *
 * THE TWO BOOLEANS ARE NOT TRI-STATE, which is why they are not a `NativeSelect`
 * like the bill sheet's `issuedManually`. There "made here" is a real third
 * answer; here there is no question that `openOnly=false` asks — it is simply
 * the unfiltered register, and writing it into the URL would split the cache,
 * inflate the "More filters (n)" badge and travel inside any link the owner
 * sends someone. Each control therefore sets `true` or `undefined`, never
 * `false`, and `useChallanFilters`' `setFilters` deletes on `false` as a second
 * line of defence.
 * ═════════════════════════════════════════════════════════════════════════════
 */

/* -------------------------------------------------------------------------- */
/* The words for the enums                                                     */
/* -------------------------------------------------------------------------- */

/**
 * `ChallanType` → the sentence the owner reads. NEVER the enum.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THESE LIVE IN THE FILTER FILE ON PURPOSE. This is the file that MUST enumerate
 * every type, because the select has to offer all of them; the register imports
 * the same map for its rows, so the word on a row and the word in the filter
 * that produced it cannot drift into two different phrasings for one Rule 55
 * reason.
 *
 * `Record<ChallanType, string>` rather than a loose object: `CHALLAN_TYPES` is
 * an `ALTER TABLE` away from growing a value (CLAUDE.md), and when it does, this
 * fails to compile instead of quietly rendering `INTER_UNIT_TRANSFER` in a
 * select in front of a 55-year-old fabricator.
 *
 * THE WORDING IS THE MOVEMENT, NOT THE CATEGORY. Rule 55(1) makes the reason the
 * legal basis for goods travelling without a tax invoice, so "Sent for job work"
 * beats "Job work (out)": one says what happened, the other names a bucket.
 * ─────────────────────────────────────────────────────────────────────────────
 */
const CHALLAN_TYPE_KEY: Record<ChallanType, string> = {
  JOB_WORK_OUT: "challan.typeJobWorkOut",
  JOB_WORK_IN: "challan.typeJobWorkIn",
  SUPPLY_NOT_INVOICED: "challan.typeSupplyNotInvoiced",
  APPROVAL: "challan.typeApproval",
  RETURNABLE: "challan.typeReturnable",
  OTHER: "challan.typeOther",
}

/** `useChallanTypeLabel()` → `(type) => "Sent for job work"`. */
export function useChallanTypeLabel(): (type: ChallanType) => string {
  const { t } = useTranslation()
  return (type: ChallanType) => t(CHALLAN_TYPE_KEY[type])
}

/**
 * `direction` → the words, and this one is not decoration.
 *
 * The server derives it from the type (`INWARD` for `JOB_WORK_IN` alone) and
 * sends it on every row. On an inward challan the CUSTOMER is the consigner and
 * this business is the consignee — the two address blocks swap on the printed
 * page — so a register that says "sent to" against an inward row is describing
 * the opposite of what happened. Never recompute this from `challanType` here:
 * the second copy is precisely the one that could disagree with the first.
 */
const CHALLAN_DIRECTION_KEY: Record<ChallanDirection, string> = {
  OUTWARD: "challan.directionOutward",
  INWARD: "challan.directionInward",
}

/** `useChallanDirectionLabel()` → `(direction) => "Received in"`. */
export function useChallanDirectionLabel(): (direction: ChallanDirection) => string {
  const { t } = useTranslation()
  return (direction: ChallanDirection) => t(CHALLAN_DIRECTION_KEY[direction])
}

/* -------------------------------------------------------------------------- */
/* The sheet                                                                   */
/* -------------------------------------------------------------------------- */

export function ChallanFilterSheet({
  filters,
  setFilters,
  clearFilters,
  activeCount,
}: {
  filters: ReturnType<typeof useChallanFilters>["filters"]
  setFilters: ReturnType<typeof useChallanFilters>["setFilters"]
  clearFilters: () => void
  activeCount: number
}): React.JSX.Element {
  const { t } = useTranslation()
  const typeLabel = useChallanTypeLabel()
  const [open, setOpen] = useState(false)
  const [pickingCustomer, setPickingCustomer] = useState(false)

  /*
   * The URL holds only the id, so the NAME has to be fetched to show it. Without
   * this the button would read "Customer #4", which is the database's way of
   * identifying Lupin, not the owner's.
   */
  const chosen = useCustomer(
    filters.customerId === undefined ? undefined : String(filters.customerId),
  )

  const activePreset = activeDatePreset(filters.from, filters.to)

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          setOpen(true)
        }}
      >
        <FilterIcon />
        {/* The count is the whole point of collapsing them: a filtered register
          * that LOOKS unfiltered is how someone concludes their challans are
          * gone. */}
        {activeCount === 0
          ? t("list.moreFilters")
          : t("list.moreFiltersCount", { n: activeCount })}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-4 overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("list.moreFilters")}</DialogTitle>
            <DialogDescription>{t("challan.listSubtitle")}</DialogDescription>
          </DialogHeader>

          {/* ── Show only ────────────────────────────────────────────────── */}
          {/*
            * §17.7'S TWO QUESTIONS, and the whole reason the feature exists.
            * Each has help text under it because the label alone cannot carry
            * the distinction that matters: "delivered, not yet billed" is NOT
            * the same set as Status = Still open, and offering both under
            * similar words is how a signal stops being believed.
            */}
          <div className="flex flex-col gap-3">
            <Label>{t("challan.showOnly")}</Label>

            <FilterToggle
              label={t("list.onlyUnbilled")}
              help={t("list.onlyUnbilledHelp")}
              on={filters.openOnly === true}
              onToggle={(on) => {
                // `true` or NOTHING. Never `false` — see the block comment above.
                setFilters({ openOnly: on ? true : undefined })
              }}
            />

            <FilterToggle
              label={t("list.onlyOverdue")}
              help={t("list.onlyOverdueHelp")}
              on={filters.overdueOnly === true}
              onToggle={(on) => {
                setFilters({ overdueOnly: on ? true : undefined })
              }}
            />
          </div>

          {/* ── Why the goods moved ──────────────────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="challan-type-filter">{t("challan.typeLabel")}</Label>
            <NativeSelect
              id="challan-type-filter"
              className="w-full"
              value={filters.challanType ?? ""}
              onChange={(event) => {
                setFilters({
                  challanType: event.target.value === "" ? undefined : event.target.value,
                })
              }}
            >
              <NativeSelectOption value="">{t("list.anyChallanType")}</NativeSelectOption>
              {/* Driven off the tuple in `@steelbill/types`, so a new Rule 55
                * reason appears here the day it is added rather than being
                * unreachable until somebody remembers this file. */}
              {CHALLAN_TYPES.map((type) => (
                <NativeSelectOption key={type} value={type}>
                  {typeLabel(type)}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </div>

          {/* ── Customer ─────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <Label>{t("lbl.customer")}</Label>
            {filters.customerId === undefined ? (
              <Button
                type="button"
                variant="outline"
                className="justify-start"
                onClick={() => {
                  setPickingCustomer(true)
                }}
              >
                {t("list.filterByCustomer")}
              </Button>
            ) : (
              <div className="flex items-center justify-between gap-2 rounded-md border p-2">
                <span className="min-w-0 truncate text-sm">
                  {chosen.data?.name ?? t("msg.loading")}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setFilters({ customerId: undefined })
                  }}
                >
                  <XIcon className="size-4" />
                  <span className="sr-only">{t("list.clearCustomerFilter")}</span>
                </Button>
              </div>
            )}
          </div>

          {/* ── Challan date ─────────────────────────────────────────────── */}
          {/* The date the goods MOVED (`challan_date`) — not the expected return
            * date and not the date a bill was raised against it. Presets come
            * from `lib/date-presets`, shared with the other two registers so
            * "This year" cannot come to mean two things. */}
          <div className="flex flex-col gap-2">
            <Label>{t("list.challanDate")}</Label>

            <div className="flex flex-wrap gap-2">
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.id}
                  type="button"
                  variant={activePreset?.id === preset.id ? "default" : "outline"}
                  onClick={() => {
                    setFilters(preset.range())
                  }}
                >
                  {t(preset.labelKey)}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="challan-filter-from" className="text-muted-foreground text-xs">
                  {t("list.dateFrom")}
                </Label>
                {/* The app's own calendar, never `<input type="date">` — that
                  * renders in the BROWSER's locale, so a Marathi screen opened
                  * an English picker (§11.4). */}
                <DateField
                  id="challan-filter-from"
                  clearable
                  value={filters.from ?? ""}
                  placeholder={t("list.dateFrom")}
                  onChange={(value) => {
                    setFilters({ from: value === "" ? undefined : value })
                  }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="challan-filter-to" className="text-muted-foreground text-xs">
                  {t("list.dateTo")}
                </Label>
                <DateField
                  id="challan-filter-to"
                  clearable
                  value={filters.to ?? ""}
                  placeholder={t("list.dateTo")}
                  onChange={(value) => {
                    setFilters({ to: value === "" ? undefined : value })
                  }}
                />
              </div>
            </div>
          </div>

          {/* ── Amount ───────────────────────────────────────────────────── */}
          {/* The declared value of what moved, not a debt — see the header. The
            * control itself is `components/RupeeBound`, shared with the other two
            * sheets: the owner types rupees, the URL and the API carry integer
            * paise, and an empty box is "no filter" rather than zero. */}
          <div className="flex flex-col gap-1.5">
            <Label>{t("list.amountRange")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <RupeeBound
                id="challan-filter-min-amount"
                label={t("list.amountFrom")}
                paise={filters.minTotalPaise}
                onChange={(minTotalPaise) => {
                  setFilters({ minTotalPaise })
                }}
              />
              <RupeeBound
                id="challan-filter-max-amount"
                label={t("list.amountTo")}
                paise={filters.maxTotalPaise}
                onChange={(maxTotalPaise) => {
                  setFilters({ maxTotalPaise })
                }}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              disabled={activeCount === 0}
              onClick={() => {
                clearFilters()
              }}
            >
              {t("list.clearFilters")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setOpen(false)
              }}
            >
              {t("btn.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerPicker
        open={pickingCustomer}
        onOpenChange={setPickingCustomer}
        onPick={(customer) => {
          setFilters({ customerId: customer.id })
        }}
      />
    </>
  )
}

/**
 * One of §17.7's two narrowings: on, or absent.
 *
 * A FULL-WIDTH BUTTON RATHER THAN A CHECKBOX, for the same reason the date
 * presets in this dialog are buttons: it is the largest, most obviously
 * pressable target on a phone held in a workshop, and `aria-pressed` gives a
 * screen reader the on/off state a bare button would not carry.
 *
 * The help line is not optional. Both of these look like they could be answered
 * by the Status select and neither can be, and a control the owner half-trusts
 * gets used once.
 */
function FilterToggle({
  label,
  help,
  on,
  onToggle,
}: {
  label: string
  help: string
  on: boolean
  onToggle: (on: boolean) => void
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Button
        type="button"
        variant={on ? "default" : "outline"}
        aria-pressed={on}
        className="h-auto justify-start py-2 text-left whitespace-normal"
        onClick={() => {
          onToggle(!on)
        }}
      >
        {/* A tick as well as the filled variant — colour alone is not a state
          * (§11.1), and this dialog is read in workshop daylight. */}
        {on ? <CheckIcon /> : null}
        {label}
      </Button>
      <p className="text-muted-foreground text-xs">{help}</p>
    </div>
  )
}

/**
 * The filters currently on, as removable chips under the toolbar.
 *
 * The status is NOT a chip: it has its own visible select in the toolbar, and
 * repeating it here would let the owner clear it from one place while the other
 * still shows it set. Everything that lives inside the dialog IS a chip — the
 * two booleans especially, because `routes.challansUnbilled` links into this
 * register with `openOnly` already on, and a filter arriving from somewhere else
 * with nothing on screen to say so is how a register gets mistrusted.
 */
export function ActiveChallanFilterChips({
  filters,
  setFilters,
}: {
  filters: ReturnType<typeof useChallanFilters>["filters"]
  setFilters: ReturnType<typeof useChallanFilters>["setFilters"]
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const typeLabel = useChallanTypeLabel()
  const chosen = useCustomer(
    filters.customerId === undefined ? undefined : String(filters.customerId),
  )

  const chips: { id: string; label: string; clear: () => void }[] = []

  if (filters.openOnly === true) {
    chips.push({
      id: "openOnly",
      label: t("list.onlyUnbilled"),
      clear: () => {
        setFilters({ openOnly: undefined })
      },
    })
  }
  if (filters.overdueOnly === true) {
    chips.push({
      id: "overdueOnly",
      label: t("list.onlyOverdue"),
      clear: () => {
        setFilters({ overdueOnly: undefined })
      },
    })
  }
  if (filters.challanType !== undefined) {
    chips.push({
      id: "challanType",
      /*
       * Through the same map the select and the rows use. A raw
       * `SUPPLY_NOT_INVOICED` in a chip is the database identifier leaking onto
       * the screen, and it is unreadable to the person the app is for.
       *
       * A hand-edited `?challanType=NONSENSE` falls back to the raw value rather
       * than an empty chip: the server will refuse it, and a blank chip would
       * say a filter is on without saying which.
       */
      label: isChallanType(filters.challanType)
        ? typeLabel(filters.challanType)
        : filters.challanType,
      clear: () => {
        setFilters({ challanType: undefined })
      },
    })
  }
  if (filters.customerId !== undefined) {
    chips.push({
      id: "customer",
      label: chosen.data?.name ?? t("msg.loading"),
      clear: () => {
        setFilters({ customerId: undefined })
      },
    })
  }
  if (filters.from !== undefined || filters.to !== undefined) {
    chips.push({
      id: "dates",
      /* Through `formatDay`, so the chip reads "1 Aug 2026" like every other
       * date in the app. A raw `2026-08-01` here is the wire format leaking
       * onto the screen, and it reads as a different date to a person than the
       * rows it is filtering. */
      label: `${filters.from === undefined ? "…" : formatDay(filters.from)} → ${
        filters.to === undefined ? "…" : formatDay(filters.to)
      }`,
      clear: () => {
        setFilters({ from: undefined, to: undefined })
      },
    })
  }
  if (filters.minTotalPaise !== undefined || filters.maxTotalPaise !== undefined) {
    chips.push({
      id: "amount",
      /* Through `formatRupees`, so the chip reads "₹1,45,600.00" with Indian
       * digit grouping like every other amount in the app — never
       * `toLocaleString`, and never the raw paise integer, which is the wire
       * format leaking onto the screen as a number a hundred times too big. */
      label: `${
        filters.minTotalPaise === undefined ? "…" : formatRupees(filters.minTotalPaise)
      } → ${filters.maxTotalPaise === undefined ? "…" : formatRupees(filters.maxTotalPaise)}`,
      /* BOTH ENDS COME OFF TOGETHER. The chip shows one range, so clearing it
       * must leave no half-range behind narrowing the register invisibly. */
      clear: () => {
        setFilters({ minTotalPaise: undefined, maxTotalPaise: undefined })
      },
    })
  }

  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => (
        /*
         * Each chip is REMOVABLE where it is shown. Sending someone back into
         * the dialog to undo one filter is the reason people give up and clear
         * everything, losing the two they wanted.
         */
        <Badge key={chip.id} variant="secondary" className="gap-1 py-1 pr-1 pl-2">
          <span className="max-w-48 truncate">{chip.label}</span>
          <button
            type="button"
            onClick={chip.clear}
            aria-label={t("list.clearFilters")}
            className="hover:bg-background/60 rounded-sm p-0.5"
          >
            <XIcon className="size-3" />
          </button>
        </Badge>
      ))}
    </div>
  )
}

/** The URL is a string; this is what makes it safe to index the label map. */
function isChallanType(value: string): value is ChallanType {
  return (CHALLAN_TYPES as readonly string[]).includes(value)
}
